import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, ChevronRight, Camera, ImageIcon, Loader2, ZoomIn, ZoomOut } from 'lucide-react';
import { compressImage, uploadFileWithProgress } from './lib/storage.js';
import { shortId } from './lib/job-helpers.js';
import { retakeInspectionPhoto } from './lib/inspection-photo-retake.js';
import './inspection-photo-review.css';

export default function InspectionPhotoReview({ photos, initialKey, jobId, onReplace, onClose }) {
  const dialogRef = useRef(null);
  const replaceRef = useRef(onReplace);
  replaceRef.current = onReplace;
  const [selectedKey, setSelectedKey] = useState(initialKey);
  const [zoomed, setZoomed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [error, setError] = useState(null);
  const [imageFailed, setImageFailed] = useState(false);
  const index = Math.max(0, photos.findIndex((entry) => entry.key === selectedKey));
  const entry = photos[index];

  useEffect(() => {
    const dialog = dialogRef.current;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialog.showModal();
    return () => {
      dialog.close();
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus?.();
    };
  }, []);

  useEffect(() => { setImageFailed(false); }, [entry?.photo.url]);

  function select(next) {
    setSelectedKey(photos[next].key);
    setZoomed(false);
    setError(null);
  }

  async function retake(file) {
    if (!file || busy || !entry) return;
    setBusy(true);
    setError(null);
    setUploadPct(0);
    try {
      await retakeInspectionPhoto(entry, file, {
        upload: async (image) => {
          const blob = await compressImage(image);
          const id = shortId();
          const path = `inspections/${jobId}/retakes/${id}.jpg`;
          const url = await uploadFileWithProgress(path, blob, { contentType: 'image/jpeg' },
            (progress) => setUploadPct(Math.round(progress * 100)));
          return { id, url, path, createdAt: Date.now() };
        },
        save: (original, replacement) => replaceRef.current(original, replacement),
      });
      setZoomed(false);
    } catch (err) {
      // Keep both files if persistence failed: a lost acknowledgement may
      // mean the server saved the replacement even though the call rejected.
      setError(`Could not replace the photo. Your original has not been deleted. ${err.message}`);
    } finally {
      setBusy(false);
    }
  }

  return createPortal(
    <dialog
      ref={dialogRef}
      className="inspection-photo-review"
      aria-label="Review inspection photos"
      onCancel={(event) => { event.preventDefault(); if (!busy) onClose(); }}
      onKeyDown={(event) => {
        if (busy || event.target.tagName === 'INPUT') return;
        if (event.key === 'ArrowLeft' && index > 0) { event.preventDefault(); select(index - 1); }
        if (event.key === 'ArrowRight' && index < photos.length - 1) { event.preventDefault(); select(index + 1); }
      }}
    >
      <header className="inspection-photo-review-header">
        <div>
          <h2>Inspection photos</h2>
          <p aria-live="polite">{index + 1} of {photos.length} · {entry?.label}</p>
        </div>
        <button type="button" onClick={onClose} disabled={busy} aria-label="Close photo review"><X /></button>
      </header>
      <div className={`inspection-photo-stage${zoomed ? ' zoomed' : ''}`}>
        {entry && <img src={entry.photo.url} alt={entry.label} onError={() => setImageFailed(true)} />}
        {imageFailed && <p role="alert">Photo could not load. Check your connection and reopen the viewer.</p>}
      </div>
      {entry?.note && <p className="inspection-photo-note">{entry.note}</p>}
      {error && <p className="inspection-photo-error" role="alert">{error}</p>}
      <footer className="inspection-photo-review-controls">
        <button type="button" disabled={busy || index === 0} onClick={() => select(index - 1)} aria-label="Previous photo"><ChevronLeft /> Previous</button>
        <button type="button" disabled={busy} onClick={() => setZoomed(!zoomed)} aria-label={zoomed ? 'Zoom out' : 'Zoom in'}>
          {zoomed ? <ZoomOut /> : <ZoomIn />} {zoomed ? 'Fit' : 'Zoom'}
        </button>
        <button type="button" disabled={busy || index >= photos.length - 1} onClick={() => select(index + 1)} aria-label="Next photo">Next <ChevronRight /></button>
        {onReplace && <>
        <label className={`inspection-photo-retake${busy ? ' busy' : ''}`}>
          <input type="file" accept="image/*" capture="environment" disabled={busy}
            aria-label="Retake photo with camera"
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = '';
              retake(file);
            }} />
          {busy ? <><Loader2 className="spin" /> Saving {uploadPct}%…</> : <><Camera /> Retake</>}
        </label>
        <label className={`inspection-photo-retake${busy ? ' busy' : ''}`}>
          <input type="file" accept="image/*" disabled={busy} aria-label="Replace photo from library"
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = '';
              retake(file);
            }} />
          <ImageIcon /> Replace
        </label>
        </>}
      </footer>
    </dialog>, document.body,
  );
}
